// Sync metadata helpers — per-row updatedAt + tombstone deletes.
//
// Why: cloud sync used to overwrite the entire entity array with whichever
// device synced last. A user editing invoice X on device A and invoice Y on
// device B would lose one of those edits depending on sync order. With per-row
// updatedAt + merge, both edits survive because we keep the row whose
// updatedAt is newest.
//
// How deletes survive: deleted rows are kept in the array as tombstones
// ({ id, _deleted: true, updatedAt }) so the merge can tell "this row was
// deleted at time T" apart from "this row never existed on this device".
// Without tombstones a delete on device A would resurrect on next sync from
// device B. Selectors filter tombstones out — UI never sees them.
//
// Backwards compat: rows without updatedAt are treated as updatedAt = 0.
// Rows without _deleted are alive. First write after upgrade stamps the field.

// Stamp a row with the current time. Returns a NEW object (do not mutate).
// Use on every add/edit before persisting.
export const stampRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  return { ...row, updatedAt: Date.now() };
};

// Stamp every row in an array. Used when bulk-editing or when migrating
// pre-upgrade rows to carry an updatedAt going forward.
export const stampRows = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map(stampRow);
};

// Build a tombstone for a deleted row. Pushed into state.data in place of
// the live row so the deletion is visible to the merge layer.
export const tombstoneFor = (id) => ({
  id,
  _deleted: true,
  updatedAt: Date.now(),
});

// True if the row is not a tombstone. Use as a `.filter` predicate in
// selectors so UI never sees deleted rows.
export const isAlive = (row) => !!row && !row._deleted;

// Filter a (possibly mixed) array down to live rows only.
export const liveRows = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.filter(isAlive);
};

// Per-row merge by `id` and `updatedAt`. The row with the higher updatedAt
// wins; a tombstone wins ties (deletes settle slightly faster than edits,
// which matches user intent — if you saw "deleted" once, prefer it sticking).
//
// Rows without an id are passed through from local only (we can't merge
// what we can't key). This shouldn't happen for our entities — every row
// has an id from nanoid — but is defensive against historical data.
export const mergeRowsById = (localArr, cloudArr) => {
  const local = Array.isArray(localArr) ? localArr : [];
  const cloud = Array.isArray(cloudArr) ? cloudArr : [];

  const byId = new Map();
  const noId = [];

  const consider = (row) => {
    if (!row || typeof row !== 'object') return;
    if (row.id == null) {
      noId.push(row);
      return;
    }
    const incomingTs = Number(row.updatedAt) || 0;
    const existing = byId.get(row.id);
    if (!existing) {
      byId.set(row.id, row);
      return;
    }
    const existingTs = Number(existing.updatedAt) || 0;
    if (incomingTs > existingTs) {
      byId.set(row.id, row);
    } else if (incomingTs === existingTs && row._deleted && !existing._deleted) {
      // Tie: prefer the tombstone over the live row
      byId.set(row.id, row);
    }
  };

  for (const row of local) consider(row);
  for (const row of cloud) consider(row);

  return [...byId.values(), ...noId];
};
